import logger from "@/utils/logger";
/**
 * Audio Manager - Handles notification sounds with browser autoplay policy workaround
 *
 * Modern browsers block audio playback unless there's been a user interaction.
 * This manager initializes audio on first user interaction and maintains the context.
 */

class AudioManager {
    private initialized = false;
    private audioContext: AudioContext | null = null;

    /**
     * Initialize audio context on first user interaction
     * Call this on any user click/touch event
     */
    initialize() {
        if (this.initialized) return;

        try {
            // Create audio context (this requires user gesture)
            const AudioContextClass =
                window.AudioContext ||
                (
                    window as typeof window & {
                        webkitAudioContext: typeof AudioContext;
                    }
                ).webkitAudioContext;
            this.audioContext = new AudioContextClass();

            // Play silent sound to "unlock" audio
            const oscillator = this.audioContext.createOscillator();
            oscillator.frequency.value = 0;
            oscillator.connect(this.audioContext.destination);
            oscillator.start(0);
            oscillator.stop(0.001);

            this.initialized = true;
            logger.info("🔔 Audio Manager: Initialized successfully");
        } catch (error) {
            console.warn("Audio Manager: Failed to initialize", error);
        }
    }

    /**
     * Play notification bell sound
     * Will work reliably after initialize() has been called
     */
    async playNotificationBell(): Promise<boolean> {
        try {
            const audio = new Audio("/notification-bell.wav");
            audio.volume = 0.6;

            await audio.play();
            logger.info("🔔 Notification bell played");
            return true;
        } catch (error) {
            if (error instanceof Error) {
                if (error.name === "NotAllowedError") {
                    logger.info(
                        "🔇 Sound blocked - waiting for user interaction"
                    );
                } else {
                    console.warn("🔇 Sound playback error:", error.message);
                }
            }
            return false;
        }
    }

    /**
     * Check if audio is initialized and ready
     */
    isReady(): boolean {
        return this.initialized;
    }
}

// Export singleton instance
export const audioManager = new AudioManager();

// Auto-initialize on any user interaction
if (typeof window !== "undefined") {
    const initOnInteraction = () => {
        audioManager.initialize();
        // Remove listeners after first initialization
        document.removeEventListener("click", initOnInteraction);
        document.removeEventListener("touchstart", initOnInteraction);
        document.removeEventListener("keydown", initOnInteraction);
    };

    document.addEventListener("click", initOnInteraction, { once: true });
    document.addEventListener("touchstart", initOnInteraction, { once: true });
    document.addEventListener("keydown", initOnInteraction, { once: true });
}
